import { CareOSLoader } from '@/components/ui/careos-loader';
import { useEffect, useRef, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import L from 'leaflet';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, MapPin } from 'lucide-react';

// Dynamically load Leaflet CSS only when the map is used
const ensureLeafletCss = () => {
  if (typeof document === 'undefined') return;
  const id = 'leaflet-css';
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
  link.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
  link.crossOrigin = '';
  document.head.appendChild(link);
};

// Fix default Leaflet icon
const fixLeafletIcon = () => {
  delete (L.Icon.Default.prototype as any)._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  });
};
ensureLeafletCss();
fixLeafletIcon();

const SHADOW_URL = 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png';

function createColoredIcon(color: 'green' | 'red' | 'gray') {
  const hue = color === 'green' ? '130' : color === 'red' ? '0' : '0';
  const sat = color === 'gray' ? '0%' : '70%';
  const light = color === 'gray' ? '60%' : color === 'red' ? '45%' : '35%';
  const fill = `hsl(${hue}, ${sat}, ${light})`;
  const stroke = color === 'gray' ? 'hsl(0,0%,40%)' : color === 'red' ? 'hsl(0,70%,30%)' : 'hsl(130,70%,20%)';

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="25" height="41" viewBox="0 0 25 41">
    <path d="M12.5 0C5.6 0 0 5.6 0 12.5C0 21.9 12.5 41 12.5 41S25 21.9 25 12.5C25 5.6 19.4 0 12.5 0z" fill="${fill}" stroke="${stroke}" stroke-width="1"/>
    <circle cx="12.5" cy="12.5" r="5" fill="white" opacity="0.9"/>
  </svg>`;

  return L.icon({
    iconUrl: `data:image/svg+xml;base64,${btoa(svg)}`,
    shadowUrl: SHADOW_URL,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
  });
}

const greenIcon = createColoredIcon('green');
const redIcon = createColoredIcon('red');
const grayIcon = createColoredIcon('gray');

interface MapPatient {
  patientId: string;
  patientName: string;
  lat: number;
  lng: number;
  caregiverName: string | null;
  hasActiveShift: boolean;
  hasIncident: boolean;
}

interface CompanyMapViewProps {
  companyId: string;
}

export function CompanyMapView({ companyId }: CompanyMapViewProps) {
  const { t } = useTranslation();
  const mapInstanceRef = useRef<L.Map | null>(null);
  const markersRef = useRef<L.Marker[]>([]);
  const observerRef = useRef<ResizeObserver | null>(null);

  const { data: mapPatients = [], isLoading } = useQuery({
    queryKey: ['company-map-data', companyId],
    queryFn: async (): Promise<MapPatient[]> => {
      // 1. Get company patient assignments
      const { data: assignments } = await supabase
        .from('company_patient_assignments')
        .select('patient_id')
        .eq('company_id', companyId)
        .eq('is_active', true);

      if (!assignments?.length) return [];

      const patientIds = assignments.map(a => a.patient_id);

      // 2. Get patient details with coordinates
      const { data: patients } = await supabase
        .from('patients')
        .select('id, nickname, residence_latitude, residence_longitude')
        .in('id', patientIds);

      if (!patients?.length) return [];

      // 3. Get active shifts with check-in coordinates
      const { data: activeShifts } = await supabase
        .from('shifts')
        .select('id, patient_id, caregiver_id, check_in_latitude, check_in_longitude')
        .in('patient_id', patientIds)
        .eq('status', 'active');

      // 4. Get caregiver names
      const caregiverIds = [...new Set((activeShifts || []).map(s => s.caregiver_id))];
      let caregiverNames: Record<string, string> = {};
      if (caregiverIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('user_id, full_name')
          .in('user_id', caregiverIds);
        if (profiles) {
          caregiverNames = Object.fromEntries(profiles.map(p => [p.user_id, p.full_name || '']));
        }
      }

      // 5. Get incident counts for active shifts
      const activeShiftIds = (activeShifts || []).map(s => s.id);
      const incidentsByShift: Record<string, number> = {};
      if (activeShiftIds.length > 0) {
        const { data: incidents } = await supabase
          .from('task_executions')
          .select('shift_id')
          .in('shift_id', activeShiftIds)
          .eq('adhoc_task_type', 'incident');

        if (incidents) {
          for (const inc of incidents) {
            if (inc.shift_id) {
              incidentsByShift[inc.shift_id] = (incidentsByShift[inc.shift_id] || 0) + 1;
            }
          }
        }

        // Also check for incident task_type through care_plan_tasks
        const { data: linkedIncidents } = await supabase
          .from('task_executions')
          .select('shift_id, task_id')
          .in('shift_id', activeShiftIds)
          .not('task_id', 'is', null);

        if (linkedIncidents?.length) {
          const taskIds = [...new Set(linkedIncidents.map(i => i.task_id!))];
          const { data: incidentTasks } = await supabase
            .from('care_plan_tasks')
            .select('id')
            .in('id', taskIds)
            .eq('task_type', 'incident');

          if (incidentTasks) {
            const incidentTaskIds = new Set(incidentTasks.map(t => t.id));
            for (const exec of linkedIncidents) {
              if (exec.task_id && incidentTaskIds.has(exec.task_id) && exec.shift_id) {
                incidentsByShift[exec.shift_id] = (incidentsByShift[exec.shift_id] || 0) + 1;
              }
            }
          }
        }
      }

      // 6. Build map data
      const result: MapPatient[] = [];

      for (const patient of patients) {
        const shift = (activeShifts || []).find(s => s.patient_id === patient.id);

        if (shift && shift.check_in_latitude && shift.check_in_longitude) {
          result.push({
            patientId: patient.id,
            patientName: patient.nickname,
            lat: shift.check_in_latitude,
            lng: shift.check_in_longitude,
            caregiverName: caregiverNames[shift.caregiver_id] || null,
            hasActiveShift: true,
            hasIncident: (incidentsByShift[shift.id] || 0) > 0,
          });
        } else if (patient.residence_latitude && patient.residence_longitude) {
          result.push({
            patientId: patient.id,
            patientName: patient.nickname,
            lat: patient.residence_latitude,
            lng: patient.residence_longitude,
            caregiverName: null,
            hasActiveShift: false,
            hasIncident: false,
          });
        }
      }

      return result;
    },
    enabled: !!companyId,
    refetchInterval: 120000,
    refetchOnWindowFocus: false,
    staleTime: 60000,
  });

  // Callback ref: initializes the map when the div is first mounted in the DOM
  const mapRefCallback = useCallback((node: HTMLDivElement | null) => {
    // Cleanup previous instance if node changes
    if (!node) {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
      return;
    }

    // Already initialized on this node
    if (mapInstanceRef.current) {
      mapInstanceRef.current.invalidateSize();
      return;
    }

    const map = L.map(node).setView([-23.5505, -46.6333], 10);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);

    mapInstanceRef.current = map;

    // Use ResizeObserver to detect when the container becomes visible
    const observer = new ResizeObserver(() => {
      map.invalidateSize();
    });
    observer.observe(node);
    observerRef.current = observer;
  }, []);

  // Update markers
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    // Clear old markers
    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];

    if (mapPatients.length === 0) return;

    const bounds = L.latLngBounds([]);

    for (const p of mapPatients) {
      const icon = !p.hasActiveShift ? grayIcon : p.hasIncident ? redIcon : greenIcon;

      const incidentBadge = p.hasIncident
        ? `<div style="color:#dc2626;font-weight:600;margin-top:4px;">⚠️ ${t('company.mapIncidentAlert')}</div>`
        : '';

      const caregiverLine = p.hasActiveShift
        ? `<div style="font-size:12px;color:#666;">🩺 ${p.caregiverName || t('company.unknownCaregiver')}</div>`
        : `<div style="font-size:12px;color:#999;">🚫 ${t('company.mapNoCaregiver')}</div>`;

      const popup = `
        <div style="min-width:140px;">
          <a href="/app/patients/${p.patientId}" style="font-weight:600;font-size:14px;margin-bottom:2px;display:block;color:inherit;text-decoration:underline;cursor:pointer;">${p.patientName}</a>
          ${caregiverLine}
          ${incidentBadge}
        </div>
      `;

      const marker = L.marker([p.lat, p.lng], { icon }).addTo(map).bindPopup(popup);
      markersRef.current.push(marker);
      bounds.extend([p.lat, p.lng]);
    }

    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
  }, [mapPatients, t]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[400px]">
        <CareOSLoader size="md" label={t('common.loading')} />
      </div>
    );
  }

  if (mapPatients.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[400px] text-muted-foreground">
        <MapPin className="h-10 w-10 mb-2 opacity-40" />
        <p className="text-sm">{t('company.mapNoPatientsWithLocation')}</p>
      </div>
    );
  }

  return (
    <div className="relative w-full h-[calc(100vh-280px)] min-h-[400px] rounded-lg overflow-hidden border">
      <div ref={mapRefCallback} className="w-full h-full z-0" />
      {/* Legend */}
      <div className="absolute bottom-3 left-3 z-[400] bg-background/90 backdrop-blur-sm rounded-lg p-3 text-xs space-y-1.5 border shadow-sm">
        <div className="flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded-full bg-success" />
          <span>{t('company.activeShifts')}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded-full bg-destructive" />
          <span>{t('company.mapIncidentAlert')}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded-full bg-gray-400" />
          <span>{t('company.mapNoCaregiver')}</span>
        </div>
      </div>
    </div>
  );
}
